import React, { useEffect, useState } from 'react';
import { Button, Modal, Table } from 'react-bootstrap';
import { AppAsyncSelect, AppSelect } from '../../components/common/select';

/**
 * Dev-only harness for the select migration (route /dev/selects, not registered in a
 * production build — see App.jsx).
 *
 * Exists because the things that actually break in a react-select migration are invisible
 * to jsdom: a menu painted under a Bootstrap modal backdrop, a menu clipped by
 * .table-responsive's overflow, an option label that letter-wraps in a narrow column.
 * Checking those on the twelve real screens means twelve navigations and twelve sets of
 * fixture data; checking them here is one page.
 *
 * Delete this directory (and its Route) once Phase 3 of
 * docs/tom-select-to-react-select-migration-plan.md is signed off.
 */

const OPTIONS = [
  { id: 'b', name: 'Bravo', code: 'BRV' },
  { id: 'a', name: 'Alpha', code: 'ALP' },
  { id: 'c', name: 'Charlie', code: 'CHR' },
];

const NUMERIC_OPTIONS = [
  { id: 41, name: 'Forty One' },
  { id: 42, name: 'Forty Two' },
];

const LONG_OPTIONS = [
  { id: 'l1', name: 'Recommend Continue to Next Cycle Without Certificate' },
  { id: 'l2', name: 'Major Area of concern for Stage II' },
  { id: 'l3', name: 'Sustainability / EU / Certification #Indonesia' },
];

const MANY_OPTIONS = Array.from({ length: 60 }, (_, index) => ({
  id: `opt-${index}`,
  name: `Option number ${index + 1}`,
}));

// Big enough to exceed the 50-row paint cap, so the harness exercises it.
const HUGE_OPTIONS = Array.from({ length: 120 }, (_, index) => ({
  id: `huge-${index}`,
  name: `Client ${String(index).padStart(3, '0')}`,
}));

const Case = ({ title, note, children }) => (
  <div className="col-md-6 col-xl-4" data-case={title}>
    <div className="card h-100">
      <div className="card-body">
        <div className="fw-semibold mb-1">{title}</div>
        {note && <div className="text-muted small mb-3">{note}</div>}
        {children}
      </div>
    </div>
  </div>
);

const DevSelects = () => {
  const [values, setValues] = useState({
    plain: '',
    preselected: 'a',
    numeric: 42,
    multi: ['a', 'c'],
    multiEmpty: [],
    creatable: '',
    errored: '',
    disabled: 'a',
    required: '',
    long: 'l1',
    many: '',
    searchable: '',
    unsorted: '',
    inline: '',
    clearable: 'a',
    late: 'c',
    inModal: '',
    row1: 'a',
    row2: '',
    asyncClient: '',
    asyncPreselected: '9999',
    asyncErrored: '',
    compact: 'a',
    capped: '',
  });

  const set = (name, value) => setValues((prev) => ({ ...prev, [name]: value }));

  const [showModal, setShowModal] = useState(false);

  // Reproduces the commonest real-world sequence: a value is in form state before the
  // options request resolves. The label must appear when the options land.
  const [lateOptions, setLateOptions] = useState([]);
  useEffect(() => {
    const timer = setTimeout(() => setLateOptions(OPTIONS), 1500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <div className="page-header d-print-none">
        <div className="container-xl px-3">
          <h2 className="page-title">AppSelect harness</h2>
          <div className="text-muted mt-1">
            Dev-only. Every prop combination from the Phase 1 contract table, plus the two
            containers that clip menus. Not routed in production builds.
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl px-3">
          <div className="row g-3">
            <Case title="Plain" note="No value, default placeholder">
              <AppSelect name="plain" value={values.plain} options={OPTIONS} onChange={set} />
            </Case>

            <Case title="Preselected" note="Shows the label, not the id">
              <AppSelect name="preselected" value={values.preselected} options={OPTIONS} onChange={set} />
            </Case>

            <Case title="Numeric value" note="value={42} against numeric option ids">
              <AppSelect name="numeric" value={values.numeric} options={NUMERIC_OPTIONS} onChange={set} />
            </Case>

            <Case title="Label + required" note="Click the label — it should focus the input">
              <AppSelect
                name="required"
                label="Auditor"
                required
                value={values.required}
                options={OPTIONS}
                onChange={set}
              />
            </Case>

            <Case title="Error state" note="Red border + message, announced via aria-describedby">
              <AppSelect
                name="errored"
                label="Standard"
                error="Please choose a standard"
                value={values.errored}
                options={OPTIONS}
                onChange={set}
              />
            </Case>

            <Case title="Disabled">
              <AppSelect name="disabled" value={values.disabled} options={OPTIONS} onChange={set} disabled />
            </Case>

            <Case title="Multi" note="Chips, menu stays open between picks">
              <AppSelect name="multi" value={values.multi} options={OPTIONS} onChange={set} multiple />
            </Case>

            <Case title="Multi, empty">
              <AppSelect name="multiEmpty" value={values.multiEmpty} options={OPTIONS} onChange={set} multiple />
            </Case>

            <Case title="Creatable" note="Type something absent, then pick Create…">
              <AppSelect name="creatable" value={values.creatable} options={OPTIONS} onChange={set} create />
            </Case>

            <Case title="Clearable" note="Opt-in ×; off by default for TomSelect parity">
              <AppSelect name="clearable" value={values.clearable} options={OPTIONS} onChange={set} clearable />
            </Case>

            <Case title="Long labels" note="Must wrap, never clip — the reason for react-select">
              <AppSelect name="long" value={values.long} options={LONG_OPTIONS} onChange={set} />
            </Case>

            <Case title="60 options" note="Menu scrolls; type to filter">
              <AppSelect name="many" value={values.many} options={MANY_OPTIONS} onChange={set} />
            </Case>

            <Case
              title="Capped (120 options)"
              note="Paints 50 and says so — TomSelect's maxOptions default, restored"
            >
              <AppSelect name="capped" value={values.capped} options={HUGE_OPTIONS} onChange={set} />
            </Case>

            <Case title="searchField=name,code" note="Type “brv” — matches a code that is not shown">
              <AppSelect
                name="searchable"
                value={values.searchable}
                options={OPTIONS}
                onChange={set}
                searchField="name,code"
              />
            </Case>

            <Case title="Unsorted ($order)" note="Bravo, Alpha, Charlie — source order kept">
              <AppSelect
                name="unsorted"
                value={values.unsorted}
                options={OPTIONS}
                onChange={set}
                sortField={{ field: '$order' }}
              />
            </Case>

            <Case title="Inline menu" note="dropdownParent={null} — menu is not portalled">
              <AppSelect name="inline" value={values.inline} options={OPTIONS} onChange={set} dropdownParent={null} />
            </Case>

            <Case
              title="Compact (.app-select--sm)"
              note="Must match the height of the form-control-sm beside it"
            >
              {/* Guards the sizing that SalesReport/AchievementForm depends on: its filter
                  row puts this select next to form-control-sm inputs, and react-select's
                  emotion classes will out-specify a plain rule. Asserted in
                  e2e/tests/select-migration-smoke.spec.ts by comparing rendered heights. */}
              <div className="d-flex gap-2 align-items-start">
                <div className="app-select--sm" style={{ flex: 1 }} data-role="compact-select">
                  <AppSelect name="compact" value={values.compact} options={OPTIONS} onChange={set} />
                </div>
                <input
                  className="form-control form-control-sm"
                  style={{ flex: 1 }}
                  data-role="compact-sibling"
                  defaultValue="form-control-sm"
                  readOnly
                />
              </div>
            </Case>

            <Case title="Empty options">
              <AppSelect name="none" value="" options={[]} onChange={() => {}} />
            </Case>

            <Case title="Late options" note="Value 'c' is set now; options arrive after 1.5s">
              <AppSelect name="late" value={values.late} options={lateOptions} onChange={set} isLoading={!lateOptions.length} />
            </Case>
          </div>

          <h3 className="mt-4">Async (server-searched)</h3>
          <p className="text-muted small">
            Hits the real <code>/v2/crm/companies/options</code>. Watch the network tab: one
            request when the menu first opens, none while typing one character, then one
            debounced request per search — not one per keystroke.
          </p>
          <div className="row g-3">
            <Case title="Async" note="Type at least 2 characters">
              <AppAsyncSelect
                name="asyncClient"
                label="Client"
                value={values.asyncClient}
                onChange={set}
                loadUrl="/v2/crm/companies/options"
                placeholder="Search client..."
                valueField="value"
                labelField="label"
                searchField="label,name"
              />
            </Case>

            <Case title="Async, edit mode" note="initialValue shows a label with no request">
              <AppAsyncSelect
                name="asyncPreselected"
                label="Client"
                value={values.asyncPreselected}
                onChange={set}
                loadUrl="/v2/crm/companies/options"
                valueField="value"
                labelField="label"
                searchField="label,name"
                initialValue={{ value: values.asyncPreselected, label: 'Preselected client (fake)' }}
              />
            </Case>

            <Case title="Async, error + required">
              <AppAsyncSelect
                name="asyncErrored"
                label="Client"
                required
                error="Client is required"
                value={values.asyncErrored}
                onChange={set}
                loadUrl="/v2/crm/companies/options"
                valueField="value"
                labelField="label"
                searchField="label,name"
              />
            </Case>
          </div>

          <h3 className="mt-4">Inside a table</h3>
          <p className="text-muted small">
            The menu must escape <code>.table-responsive</code>&apos;s overflow rather than being
            clipped by it, and the row must not lose height.
          </p>
          <div className="card">
            <div className="table-responsive">
              <Table className="mb-0">
                <thead>
                  <tr>
                    <th style={{ minWidth: 160 }}>Standard</th>
                    <th style={{ width: 160 }}>Narrow column</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>ISO/IEC 27001</td>
                    <td>
                      <AppSelect name="row1" value={values.row1} options={LONG_OPTIONS} onChange={set} className="mb-0" />
                    </td>
                    <td className="text-muted small">Long labels in a 160px column</td>
                  </tr>
                  <tr>
                    <td>ISO 9001</td>
                    <td>
                      <AppSelect name="row2" value={values.row2} options={MANY_OPTIONS} onChange={set} className="mb-0" />
                    </td>
                    <td className="text-muted small">Last row — menu should flip upward if needed</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>

          <h3 className="mt-4">Inside a modal</h3>
          <p className="text-muted small">
            The menu must paint above the backdrop. This is the check that catches a z-index
            regression across every modal form in the app.
          </p>
          <Button onClick={() => setShowModal(true)}>Open modal</Button>

          <Modal show={showModal} onHide={() => setShowModal(false)} centered>
            <Modal.Header closeButton>
              <Modal.Title>Select inside a modal</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <AppSelect
                name="inModal"
                label="Standard"
                value={values.inModal}
                options={LONG_OPTIONS}
                onChange={set}
              />
              <AppSelect name="many" value={values.many} options={MANY_OPTIONS} onChange={set} />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="outline-secondary" onClick={() => setShowModal(false)}>Close</Button>
            </Modal.Footer>
          </Modal>

          <h3 className="mt-4">Current values</h3>
          <pre className="bg-light p-3 rounded small">{JSON.stringify(values, null, 2)}</pre>
        </div>
      </div>
    </>
  );
};

export default DevSelects;
